// CMF (Crash Modification Factor) countermeasure + benefit arithmetic for the
// Safety Dashboard. Pure functions — no fetching, no React.
//
// Method (Highway Safety Manual Part D / FHWA CMF Clearinghouse practice):
//   prevented per year = matching crashes ÷ exposure years × (1 − CMF)
//   benefit per year   = Σ over matching crashes of (1 − CMF) × unit cost[KABCO] ÷ exposure
//   PV benefit         = benefit per year × present-value factor(discount rate, service life)
//   B/C                = PV benefit ÷ countermeasure unit cost
// "Matching" = the crashes at the site (±0.1 mi, same as the tables) that the
// CMF's own crash-type / severity scope applies to — never the corridor total.
//
// Star policy ('45' default): stars ≥ 4, and a `fallback` entry is admitted
// only when its analysis has no ≥4★ entry at all. Mirrored server-side in
// utils/cmf_library.py so tests on both sides agree.
import { kabcoOf } from './insightsText';

export const CMF_POLICIES = [
  { key: '45', label: '4–5★ (3★ fallback where none exists)' },
  { key: '5', label: '5★ only' },
  { key: '3', label: '3★ and above' },
];
export const DEFAULT_DISCOUNT_RATE_PCT = 7;
const BUFFER_MI = 0.1;

/** Years of crash exposure represented by the selected dataset years. The
 *  current calendar year counts as the elapsed fraction (2026 in September
 *  ≈ 0.67), so a partial year doesn't understate the crash rate. */
export function exposureYears(datasetYears, selectedYears = [], now = new Date()) {
  const all = (datasetYears || []).map(Number).filter((y) => !Number.isNaN(y));
  const wanted = selectedYears?.length ? all.filter((y) => selectedYears.map(Number).includes(y)) : all;
  const thisYear = now.getFullYear();
  const start = new Date(thisYear, 0, 1);
  const dayFrac = Math.min(1, Math.max(1 / 365, (now - start) / (365.25 * 86400e3)));
  return +wanted.reduce((s, y) => s + (y === thisYear ? dayFrac : y > thisYear ? 0 : 1), 0).toFixed(2);
}

/** Present-value factor of $1/yr over `years` at `ratePct` (annuity). */
export function pvFactor(ratePct, years) {
  const r = (Number(ratePct) || 0) / 100;
  if (!years) return 0;
  return r === 0 ? years : (1 - Math.pow(1 + r, -years)) / r;
}

const ROR_RE = /fixed object|guardrail|guard rail|overturn|rollover|tree|pole|ditch|embankment|ran off|off road|run off|culvert|barrier|sign post|mailbox|fence|wall/i;
/** Run-off-road signature from MassDOT manner + first-harmful-event text. */
export function isRunOffRoad(c) {
  if (c.isEmbankment) return true;
  const manner = (c.manner || '').toLowerCase();
  const fh = c.firstHarmful || '';
  return manner.includes('single vehicle') && ROR_RE.test(fh);
}

/** Does this crash fall inside the CMF's crash scope? Every key must hold. */
export function crashMatches(c, filter = {}) {
  if (filter.nonAnimal && c.isAnimal) return false;
  if (filter.animal && !c.isAnimal) return false;
  if (filter.kabco && !filter.kabco.includes(kabcoOf(c))) return false;
  if (filter.runOffRoad && !isRunOffRoad(c)) return false;
  if (filter.wetSurface && !c.isWetSurface) return false;
  if (filter.night && !(c.light || '').toLowerCase().includes('dark')) return false;
  return true;
}

/** Library entries for `analysis` admitted by `policy`, library order kept
 *  (first = primary recommendation). */
export function eligibleEntries(library, analysis, policy = '45') {
  const entries = library?.analyses?.[analysis] || [];
  if (policy === '5') return entries.filter((e) => e.stars === 5);
  if (policy === '3') return entries.filter((e) => (e.stars || 0) >= 3);
  const strong = entries.filter((e) => (e.stars || 0) >= 4);
  if (strong.length) return strong;
  return entries.filter((e) => e.fallback && (e.stars || 0) >= 3);
}

const cmfFor = (entry, k) => (entry.cmfBySeverity && entry.cmfBySeverity[k] != null
  ? entry.cmfBySeverity[k] : entry.cmf);

/** Benefit of one entry over a set of crashes. */
export function entryBenefit(entry, crashes, costs, exposure) {
  const matching = { K: 0, A: 0, B: 0, C: 0, O: 0 };
  let prevented = 0;
  let benefit = 0;
  for (const c of crashes) {
    if (!crashMatches(c, entry.crashFilter)) continue;
    const k = kabcoOf(c);
    matching[k] += 1;
    const reduction = 1 - cmfFor(entry, k);
    prevented += reduction;
    benefit += reduction * (costs[k] || 0);
  }
  const n = Object.values(matching).reduce((s, v) => s + v, 0);
  const ex = exposure || 1;
  return {
    matching, matchCount: n,
    preventedPerYr: +(prevented / ex).toFixed(3),
    benefitPerYr: Math.round(benefit / ex),
  };
}

/** Stable site key; corridor-prefixed when several corridors are loaded. */
export const siteKeyOf = (site) =>
  `${site.corridor ? `${site.corridor}|` : ''}${site.analysis}-${site.direction}-${site.id}`;
const sameCorridor = (a, b) => (a?.corridor || '') === (b?.corridor || '');

/** Recommendation for one deficiency site (a run) under the current policy.
 *  Returns null-benefit rows with `excludedReason` so the UI can explain. */
export function siteRecommendation(site, crashes, library, opts) {
  const { policy = '45', exposure = 1, costs = {}, ratePct = DEFAULT_DISCOUNT_RATE_PCT,
    unitCostOverrides = {} } = opts || {};
  const key = siteKeyOf(site);
  const hits = (crashes || []).filter(
    (c) => sameCorridor(site, c)
      && c.milepost >= site.mpStart - BUFFER_MI && c.milepost <= site.mpEnd + BUFFER_MI);
  const base = { key, analysis: site.analysis, direction: site.direction, mpStart: site.mpStart,
    mpEnd: site.mpEnd, id: site.id, corridor: site.corridor, road: site.road, crashCount: hits.length };
  if (site.analysis === 'GLR') {
    return { ...base, entry: null, excludedReason: 'noCmf',
      note: 'No published CMF for sun glare — advisory: glare warning signing, alignment or vegetation screening.' };
  }
  const entries = eligibleEntries(library, site.analysis, policy);
  if (!entries.length) {
    return { ...base, entry: null, excludedReason: 'policy',
      note: `No ${site.analysis} countermeasure meets the selected CMF quality policy.` };
  }
  const existingGuardrail = hits.some((c) => c.findings?.GRW?.existingGuardrail);
  const candidates = entries.filter((e) => !(e.requires?.noExistingGuardrail && existingGuardrail));
  if (!candidates.length) {
    return { ...base, entry: null, excludedReason: 'existingGuardrail',
      note: 'A guardrail is already in place and was struck — the warrant is validated; no new installation to price.' };
  }
  const priced = candidates.map((e) => {
    const b = entryBenefit(e, hits, costs, exposure);
    const life = e.serviceLifeYr;
    const unitCost = unitCostOverrides[`${key}:${e.crfid}`] ?? e.unitCostUsd;
    const pv = Math.round(b.benefitPerYr * pvFactor(ratePct, life));
    return { ...b, entry: e, life, unitCost, pvBenefit: pv,
      bcRatio: unitCost > 0 ? +(pv / unitCost).toFixed(1) : null };
  });
  const [primary, ...alternatives] = priced;
  return { ...base, entry: primary.entry, ...primary, alternatives,
    excludedReason: primary.matchCount ? null : 'noMatchingCrashes',
    note: primary.matchCount ? null
      : `No crashes at this site match the CMF's scope (${primary.entry.appliesTo}).` };
}

/** The recommendation that covers crash `c` for finding `analysis`. */
export function recommendationForCrash(recs, c, analysis) {
  return (recs || []).find((r) => r.analysis === analysis && r.entry && sameCorridor(r, c)
    && c.milepost >= r.mpStart - BUFFER_MI && c.milepost <= r.mpEnd + BUFFER_MI
    && (analysis === 'ISD' || r.direction === c.direction)) || null;
}

/** Program table: every priced site, ranked; totals de-duplicate overlapping
 *  sites (two analyses flagging the same stretch) by taking the LARGER
 *  benefit per overlap group — the HSM warns that multiplying CMFs
 *  overstates combined effect, so we never add or multiply overlapping rows. */
export function buildProgram(recs, rankBy = 'benefit') {
  const rows = (recs || []).filter((r) => r.entry && r.benefitPerYr > 0)
    .map((r) => ({ ...r }));
  const cmp = {
    benefit: (a, b) => b.benefitPerYr - a.benefitPerYr,
    bc: (a, b) => (b.bcRatio || 0) - (a.bcRatio || 0),
    prevented: (a, b) => b.preventedPerYr - a.preventedPerYr,
  }[rankBy] || ((a, b) => b.benefitPerYr - a.benefitPerYr);
  rows.sort(cmp);
  // overlap groups on milepost interval (any direction, same corridor)
  const sortedByMp = [...rows].sort(
    (a, b) => (a.corridor || '').localeCompare(b.corridor || '') || a.mpStart - b.mpStart);
  let group = 0;
  let groupEnd = -Infinity;
  let groupCorridor = null;
  const groupOf = {};
  for (const r of sortedByMp) {
    if (groupCorridor !== (r.corridor || '') || r.mpStart - BUFFER_MI > groupEnd) {
      group += 1; groupEnd = r.mpEnd; groupCorridor = r.corridor || '';
    } else groupEnd = Math.max(groupEnd, r.mpEnd);
    groupOf[r.key] = group;
  }
  const best = {};
  for (const r of rows) {
    const g = groupOf[r.key];
    if (!best[g] || r.benefitPerYr > best[g].benefitPerYr) best[g] = r;
  }
  rows.forEach((r, i) => { r.rank = i + 1; });
  rows.forEach((r) => {
    r.overlapGroup = groupOf[r.key];
    r.countsInTotal = best[r.overlapGroup] === r;
    // which listed row "wins" this location — shown on the dimmed rows
    r.overlapsRank = r.countsInTotal ? null : best[r.overlapGroup].rank;
  });
  const counted = Object.values(best);
  const totals = {
    sites: rows.length,
    counted: counted.length,
    preventedPerYr: +counted.reduce((s, r) => s + r.preventedPerYr, 0).toFixed(2),
    benefitPerYr: counted.reduce((s, r) => s + r.benefitPerYr, 0),
    pvBenefit: counted.reduce((s, r) => s + r.pvBenefit, 0),
    unitCost: counted.reduce((s, r) => s + (r.unitCost || 0), 0),
  };
  totals.bcRatio = totals.unitCost > 0 ? +(totals.pvBenefit / totals.unitCost).toFixed(1) : null;
  return { rows, totals };
}

/** District intersection row (K/A/B/C/O mix) → benefit of `entry` per year. */
export function intersectionBenefit(mix, entry, costs, exposure) {
  if (!entry || !mix) return { preventedPerYr: 0, benefitPerYr: 0 };
  let prevented = 0;
  let benefit = 0;
  for (const k of ['K', 'A', 'B', 'C', 'O']) {
    if (!(entry.crashFilter?.kabco || ['K', 'A', 'B', 'C', 'O']).includes(k)) continue;
    const n = mix[k] || 0;
    const reduction = 1 - cmfFor(entry, k);
    prevented += n * reduction;
    benefit += n * reduction * (costs[k] || 0);
  }
  const ex = exposure || 1;
  return { preventedPerYr: +(prevented / ex).toFixed(2), benefitPerYr: Math.round(benefit / ex) };
}

export const starsText = (n) => (n ? '★'.repeat(n) + '☆'.repeat(5 - n) : '—');

/** CSV of the program table (Excel-friendly). */
export function programCsv(program, meta = {}) {
  const head = ['Rank', 'Analysis', 'Milepost start', 'Milepost end', 'Direction', 'Countermeasure',
    'CMF', 'Stars', 'Clearinghouse ID', 'Matching crashes', 'Prevented per yr', 'Benefit per yr (USD)',
    'Service life (yr)', 'PV benefit (USD)', 'Unit cost (USD)', 'B/C', 'Counts in total'];
  const esc = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [head.map(esc).join(',')];
  for (const r of program.rows) {
    lines.push([r.rank, r.analysis, r.mpStart, r.mpEnd, r.direction, r.entry.name, r.entry.cmf,
      r.entry.stars, r.entry.crfid, r.matchCount, r.preventedPerYr, r.benefitPerYr, r.life,
      r.pvBenefit, r.unitCost, r.bcRatio, r.countsInTotal ? 'yes' : 'no (overlaps a larger row)']
      .map(esc).join(','));
  }
  lines.push('');
  lines.push(`Totals (overlaps de-duplicated),,,,,,,,,,${program.totals.preventedPerYr},${program.totals.benefitPerYr},,${program.totals.pvBenefit},${program.totals.unitCost},${program.totals.bcRatio ?? ''}`);
  if (meta.note) lines.push(esc(meta.note));
  return lines.join('\n');
}
